import { useState, useRef, useEffect, useMemo } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronsRight, Maximize2, Pencil } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Typography } from '@/components/ui/typography'
import { updateCompany } from '@/services/companies.service'
import { useCanEditCompany } from '@/hooks/use-permissions'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { formatEnumValue, formatLocationDisplay } from '@/lib/formatters'
import type { Company } from '@/types/api'

interface CompanySidePeekHeaderProps {
  company: Company
  mode?: 'sheet' | 'overview'
  /** When set, status badge becomes fund-scoped (CompanyFund) instead of company-scoped. */
  fundContextId?: string | null
  onClose?: () => void
  onExpand?: () => void
}

function normalizeWebsiteUrl(url: string): string {
  const trimmed = url.trim()
  if (!trimmed) return trimmed
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://'))
    return trimmed
  return `https://${trimmed}`
}

export function CompanySidePeekHeader({
  company,
  mode = 'sheet',
  fundContextId,
  onClose,
  onExpand,
}: CompanySidePeekHeaderProps) {
  const canEditCompany = useCanEditCompany()
  const canEdit = canEditCompany(
    company.primaryOwner?.id,
    company.secondaryOwner?.id
  )
  const queryClient = useQueryClient()
  const [isEditing, setIsEditing] = useState(false)
  const [description, setDescription] = useState(company.description || '')

  const updateMutation = useMutation({
    mutationFn: (newDescription: string) =>
      updateCompany(company.id, { description: newDescription }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', company.id] })
      setIsEditing(false)
      toast.success('Description updated')
    },
    onError: () => {
      toast.error('Failed to update description')
    },
  })

  const handleSave = () => {
    if (description === (company.description || '')) {
      setIsEditing(false)
      return
    }
    updateMutation.mutate(description)
  }

  const handleCancel = () => {
    setDescription(company.description || '')
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSave()
    } else if (e.key === 'Escape') {
      handleCancel()
    }
  }

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      adjustTextareaHeight()
      textareaRef.current.focus()
      textareaRef.current.select()
    }
  }, [isEditing])

  const fundScopedStatus = useMemo(() => {
    if (!fundContextId) return null
    const match = (company.funds ?? []).find(
      (f) => f.id === fundContextId || f.abbreviation === fundContextId
    )
    return (match?.investmentStatus as unknown as string | undefined) ?? null
  }, [company.funds, fundContextId])

  const showStatus = mode !== 'overview' && !!fundScopedStatus
  const locationLabel =
    (company.location && formatLocationDisplay(company.location)) || null
  const website = company.website?.trim()
  const websiteHref = website ? normalizeWebsiteUrl(website) : null
  // Show city + state badge in sheet mode only when both parts are available
  const sheetLocationBadge =
    mode === 'sheet' && locationLabel && locationLabel.includes(',')
      ? locationLabel
      : null
  const showActions = Boolean(onClose || onExpand)

  return (
    <div className="space-y-2">
      {showActions ? (
        <div className="flex items-center justify-between gap-2">
          {onClose ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="h-7 w-7 shrink-0 text-primary"
              aria-label="Close"
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          ) : (
            <span className="h-7 w-7" />
          )}

          {onExpand ? (
            <Button
              variant="ghost"
              size="icon"
              onClick={onExpand}
              className="h-7 w-7 shrink-0 text-primary"
              aria-label="Open full company page"
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* Status and tags */}
      <div className={cn('flex flex-wrap gap-2 pb-1', showActions && 'mt-6')}>
        {showStatus ? (
          <Badge variant={fundScopedStatus === 'ACTIVE' ? 'green' : 'red'}>
            {formatEnumValue(fundScopedStatus)}
          </Badge>
        ) : null}
        {sheetLocationBadge ? (
          <Badge variant="outline">{sheetLocationBadge}</Badge>
        ) : null}
      </div>

      {/* Sectors */}
      {company.sectors.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {company.sectors.map((sector) => (
            <Badge key={sector.id} variant="outline">
              {sector.name}
            </Badge>
          ))}
        </div>
      ) : null}

      {/* Company name */}
      <Typography variant="h3" className="mb-2 break-words">
        {company.displayName}
      </Typography>

      {/* Description */}
      {isEditing ? (
        <div className="relative rounded-lg border border-primary/50 bg-primary/5 p-2 pr-8">
          <textarea
            ref={textareaRef}
            value={description}
            onChange={(e) => {
              setDescription(e.target.value)
              adjustTextareaHeight()
            }}
            onKeyDown={handleKeyDown}
            className="w-full resize-none overflow-hidden bg-transparent text-xs leading-normal text-primary/80 selection:bg-primary/10 focus:outline-none"
          />
          <Pencil className="absolute right-3 top-3 h-4 w-4 text-primary/70" />
        </div>
      ) : (
        <div
          className={cn(canEdit && 'cursor-pointer')}
          onDoubleClick={() => canEdit && setIsEditing(true)}
          title={canEdit ? 'Double-click to edit' : undefined}
        >
          <Typography
            variant="body-sm"
            className={cn('text-primary/80', !company.description && 'italic')}
          >
            {company.description || 'No description'}
          </Typography>
        </div>
      )}

      {mode === 'overview' ? (
        <div className="space-y-1.5 pt-2">
          <div className="flex items-center justify-between gap-3">
            <Typography as="span" variant="body-sm" className="text-primary/70">
              Web
            </Typography>
            {websiteHref ? (
              <a
                href={websiteHref}
                target="_blank"
                rel="noreferrer"
                className="max-w-[65%] truncate text-right text-xs font-medium text-primary/80 hover:underline"
              >
                {website}
              </a>
            ) : (
              <Typography
                as="span"
                variant="body-sm"
                className="text-primary/70"
              >
                —
              </Typography>
            )}
          </div>
          <div className="flex items-center justify-between gap-3">
            <Typography as="span" variant="body-sm" className="text-primary/70">
              Location
            </Typography>
            {locationLabel ? (
              <Typography
                as="span"
                variant="body-sm"
                className="max-w-[65%] truncate text-right text-primary/80"
              >
                {locationLabel}
              </Typography>
            ) : (
              <Typography
                as="span"
                variant="body-sm"
                className="text-primary/70"
              >
                —
              </Typography>
            )}
          </div>
        </div>
      ) : null}
    </div>
  )
}
